import { useState } from "react";
import { ZoomIn, X } from "lucide-react";

/**
 * QuestionImage - renders a clinical image attached to an MCQ with click-to-zoom.
 *
 * Props:
 *  - src: full or relative URL ("/api/images/foo.png" - we prepend backend URL)
 *  - caption: optional alt-text / description shown below the image
 */
const QuestionImage = ({ src, caption }) => {
  const [zoomed, setZoomed] = useState(false);
  if (!src) return null;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const fullSrc = src.startsWith("http") ? src : `${BACKEND_URL}${src}`;

  return (
    <>
      <div
        data-testid="question-image-wrap"
        className="my-5 rounded-2xl overflow-hidden border border-border bg-card relative group cursor-zoom-in max-w-md mx-auto"
        onClick={() => setZoomed(true)}
      >
        <img
          data-testid="question-image"
          src={fullSrc}
          alt={caption || "Clinical image for this question"}
          className="w-full h-auto block"
          loading="lazy"
        />
        <div className="absolute top-3 right-3 bg-black/60 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition">
          <ZoomIn className="w-4 h-4" />
        </div>
        {caption && (
          <p className="px-4 py-2 text-xs text-muted-foreground italic border-t border-border bg-secondary/50">
            {caption}
          </p>
        )}
      </div>

      {zoomed && (
        <div
          data-testid="question-image-zoom"
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <button
            type="button"
            data-testid="question-image-close"
            className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={fullSrc}
            alt={caption || "Clinical image enlarged"}
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
          />
        </div>
      )}
    </>
  );
};

export default QuestionImage;
